import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usersService } from "@/services/admin";
import type { UserFilters, UserUpdateDto } from "@/types/admin";

// Users Hooks
export const useUsers = (page = 1, limit = 50, filters?: UserFilters) => {
	return useQuery({
		queryKey: ["users", page, limit, filters],
		queryFn: () => usersService.getUsers(page, limit, filters),
	});
};

export const useUser = (userId: string) => {
	return useQuery({
		queryKey: ["user", userId],
		queryFn: () => usersService.getUser(userId),
		enabled: !!userId,
	});
};

export const useUpdateUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, data }: { userId: string; data: UserUpdateDto }) =>
			usersService.updateUser(userId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});
};

export const useActivateUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (userId: string) => usersService.activateUser(userId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});
};

export const useDeactivateUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
			usersService.deactivateUser(userId, reason),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});
};

export const useSuspendUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
			usersService.suspendUser(userId, reason),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});
};

export const useResetUserCounters = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (userId: string) => usersService.resetCounters(userId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			queryClient.invalidateQueries({ queryKey: ["user"] });
		},
	});
};
